import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, Clock, Calendar, Phone, MessageCircle, CheckCircle, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { blogArticles } from '../mock/mockData';
import { blogContent } from '../data/blogContent';
import SEOHead from '../components/seo/SEOHead';
import Breadcrumbs from '../components/seo/Breadcrumbs';
import InternalLinks from '../components/seo/InternalLinks';
import GoogleReviewsStrip from '../components/GoogleReviewsStrip';
import { JsonLd, articleSchema, faqSchema } from '../components/seo/schemas';
import NotFound from './NotFound';

const formatDate = (d) =>
  new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });

const QuoteCTA = ({ title }) => (
  <div className="my-10 bg-gradient-to-br from-alden-green to-alden-green-light rounded-2xl p-8 text-white" data-testid="blog-quote-cta">
    <h3 className="text-2xl font-bold mb-2">Get a Free Project Quotation</h3>
    <p className="text-white/90 mb-6">
      Our engineers will prepare a capacity-wise machinery list, cost estimate and plant layout for your project — free, within 24 hours.
    </p>
    <div className="flex flex-wrap gap-3">
      <Link to="/contact">
        <Button className="bg-white text-alden-green hover:bg-gray-100 py-6 px-6 text-base font-semibold">
          Request Project Quotation <ArrowRight className="ml-2 w-4 h-4" />
        </Button>
      </Link>
      <a
        href={`https://wa.me/919351292512?text=${encodeURIComponent(`Hi, I read your article "${title}" and need guidance for my water plant project`)}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        <Button className="bg-[#25D366] hover:bg-[#1ebe5b] text-white py-6 px-6 text-base font-semibold">
          <MessageCircle className="mr-2 w-4 h-4" /> WhatsApp Us
        </Button>
      </a>
      <a href="tel:+919351292512">
        <Button className="bg-alden-navy hover:bg-alden-navy-light text-white py-6 px-6 text-base font-semibold">
          <Phone className="mr-2 w-4 h-4" /> +91-9351292512
        </Button>
      </a>
    </div>
  </div>
);

const Section = ({ section }) => (
  <div className="mb-10">
    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{section.heading}</h2>
    {section.paras?.map((p) => (
      <p key={p.slice(0, 40)} className="text-gray-700 leading-relaxed mb-4 text-base md:text-lg">{p}</p>
    ))}
    {section.bullets && (
      <ul className="space-y-3 mb-4">
        {section.bullets.map((b) => (
          <li key={b.slice(0, 40)} className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-alden-green flex-shrink-0 mt-1" />
            <span className="text-gray-700 text-base md:text-lg">{b}</span>
          </li>
        ))}
      </ul>
    )}
    {section.table && (
      <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm mb-4">
        <table className="w-full text-sm md:text-base">
          <thead>
            <tr className="bg-alden-green text-white">
              {section.table.headers.map((h) => (
                <th key={h} className="px-4 py-3 text-left font-semibold whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {section.table.rows.map((row, idx) => (
              <tr key={row[0]} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                {row.map((cell, ci) => (
                  <td key={`${row[0]}-${ci}`} className="px-4 py-3 text-gray-700 border-t border-gray-100">{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

export default function BlogPost() {
  const { slug } = useParams();
  const meta = blogArticles.find((a) => a.slug === slug);
  const content = blogContent[slug];

  if (!meta || !content) return <NotFound />;

  const path = `/blog/${slug}`;
  const related = blogArticles.filter((a) => a.slug !== slug && a.category === meta.category);
  const others = blogArticles.filter((a) => a.slug !== slug && a.category !== meta.category);
  const relatedArticles = [...related, ...others].slice(0, 3);

  return (
    <div className="min-h-screen" data-testid="blog-post-page">
      <SEOHead
        title={meta.metaTitle}
        description={meta.metaDescription}
        path={path}
        image={meta.image}
        type="article"
      />
      <JsonLd data={articleSchema(meta, path)} />
      <JsonLd data={faqSchema(content.faqs)} />
      <Breadcrumbs items={[{ label: 'Knowledge Hub', to: '/blog' }, { label: meta.title }]} />

      {/* Article Header */}
      <section className="py-12 md:py-16 bg-gradient-to-br from-alden-green to-alden-green-light text-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="flex flex-wrap items-center gap-3 text-sm mb-4">
            <span className="bg-white/20 px-3 py-1 rounded-full font-semibold">{meta.category}</span>
            <span className="flex items-center gap-1 text-white/80"><Calendar className="w-4 h-4" />{formatDate(meta.publishDate)}</span>
            <span className="flex items-center gap-1 text-white/80"><Clock className="w-4 h-4" />{meta.readTime}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-4">{meta.title}</h1>
          <p className="text-lg text-white/90">{meta.excerpt}</p>
        </div>
      </section>

      {/* Article Body */}
      <article className="py-12 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <img
            src={meta.image}
            alt={`${meta.title} - mineral water plant guide by Alden Technologies`}
            className="w-full h-64 md:h-96 object-cover rounded-2xl shadow-lg mb-10"
          />
          {content.intro.map((p) => (
            <p key={p.slice(0, 40)} className="text-gray-700 leading-relaxed mb-4 text-base md:text-lg">{p}</p>
          ))}

          {content.sections.slice(0, 2).map((s) => (
            <Section key={s.heading} section={s} />
          ))}

          <QuoteCTA title={meta.title} />

          {content.sections.slice(2).map((s) => (
            <Section key={s.heading} section={s} />
          ))}

          {/* FAQs */}
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 mt-12">Frequently Asked Questions</h2>
          <div className="space-y-5 mb-6">
            {content.faqs.map((faq) => (
              <Card key={faq.q} className="border-2 border-gray-200 hover:border-alden-green transition-all">
                <CardContent className="p-6">
                  <h3 className="font-bold text-lg text-gray-900 mb-2 flex items-start gap-2">
                    <Info className="w-5 h-5 text-alden-green flex-shrink-0 mt-1" />
                    {faq.q}
                  </h3>
                  <p className="text-gray-700 pl-7">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <QuoteCTA title={meta.title} />
        </div>
      </article>

      <GoogleReviewsStrip />

      {/* Related Articles */}
      <section className="py-14 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">Related Guides</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {relatedArticles.map((a) => (
              <Link key={a.slug} to={`/blog/${a.slug}`} data-testid={`related-article-${a.slug}`}>
                <Card className="border-2 border-gray-200 hover:border-alden-green hover:shadow-xl transition-all h-full group overflow-hidden">
                  <div className="h-40 overflow-hidden">
                    <img src={a.image} alt={`${a.title} guide`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <CardContent className="p-5">
                    <span className="text-xs bg-alden-green/10 text-alden-green px-3 py-1 rounded-full font-semibold">{a.category}</span>
                    <h3 className="font-bold text-gray-900 mt-3 mb-2 group-hover:text-alden-green transition-colors">{a.title}</h3>
                    <span className="inline-flex items-center gap-1 text-sm font-semibold text-alden-green">
                      Read Article <ArrowRight className="w-4 h-4" />
                    </span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <InternalLinks currentPath={path} />
    </div>
  );
}
